import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Directive, ElementRef, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID } from '@angular/core';

/* This directive provides an emitter which triggers when an element is first scrolled into view
   A simple use case can toggle a boolean on emission which shows a DOM element using ngIf
   Example:
    <div class="lazy-wrapper" (appLazyComponent)="showInner=true" [intersectOptions]="{rootMargin: '0px 0px 200px 0px'}">
      <app-lazy-element *ngIf="showInner"></app-lazy-element>
    </div>

  The above will lazily load the app-lazy-element component when it's wrapper is in view, or up to 200px below the viewport.
  The wrapper can have CSS applied to reserve height and avoid layout shifts on slower devices.
*/

@Directive({
  selector: '[appLazyComponent]'
})
export class LazyComponentDirective implements OnInit, AfterViewInit {

  // Optional IntersectionObersever config to adjust viewport, margin, and threshold
  // Default to include 5% below the viewport
  @Input() public intersectOptions: IntersectionObserverInit = { rootMargin: '0% 0% 5% 0%' };

  // Provide an event emitter which emits when the element is in view
  @Output() public appLazyComponent: EventEmitter<any> = new EventEmitter();

  private _intersectionObserver?: IntersectionObserver;

  // Get the element the directive is used on
  constructor(
    private _element: ElementRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit() {
    // If browser isn't supported, emit immediately to eagerly load
    if (isPlatformBrowser(this.platformId) && !this.hasCompatibleBrowser()) {
      this.appLazyComponent.emit();
    }
  }

  ngAfterViewInit() {
    // Only register the observer if the browser supports it
    // On SSR, element won't be rendered at all
    if (isPlatformBrowser(this.platformId) && this.hasCompatibleBrowser()) {
      this._intersectionObserver = new IntersectionObserver(entries => {
        this.checkForIntersection(entries);
      }, this.intersectOptions);
      this._intersectionObserver.observe(<Element>(this._element.nativeElement));
    }
  }

  // Callback function which emits on intersection and removes observer
  private checkForIntersection = (entries: Array<IntersectionObserverEntry>) => {
    entries.forEach((entry: IntersectionObserverEntry) => {
        if (this.checkIfIntersecting(entry)) {
            this.appLazyComponent.emit();
            this._intersectionObserver.unobserve(<Element>(this._element.nativeElement));
            this._intersectionObserver.disconnect();
        }
    });
  }

  // Check if an interesected element is this element
  private checkIfIntersecting (entry: IntersectionObserverEntry) {
      return (<any>entry).isIntersecting && entry.target === this._element.nativeElement;
  }

  // Check if browser supports intersection observer
  public hasCompatibleBrowser(): boolean {
    const hasIntersectionObserver = 'IntersectionObserver' in window;
    const userAgent = window.navigator.userAgent;
    const matches = userAgent.match(/Edge\/(\d*)\./i);

    const isEdge = !!matches && matches.length > 1;
    const isEdgeVersion16OrBetter = isEdge && (!!matches && parseInt(matches[1], 10) > 15);

    return hasIntersectionObserver && (!isEdge || isEdgeVersion16OrBetter);
  }
}
